import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import { translations, type Language } from './translations'; // hmr-bust-20260209


interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  /**
   * Count-aware lookup. For `tCount('foo', 1)` we try `foo_one` first
   * (or the CLDR plural category for the active locale), falling back
   * to `foo_other` or the bare `foo` key. `{{count}}` is substituted
   * automatically in the resolved string.
   */
  tCount: (key: string, count: number) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const STORAGE_KEY = 'flow-weaver-language';

const LOCALE_MAP: Record<Language, string> = {
  sv: 'sv-SE',
  en: 'en-US',
  no: 'nb-NO',
  da: 'da-DK',
  de: 'de-DE',
  fr: 'fr-FR',
  hu: 'hu-HU',
};

function getInitialLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored in translations) return stored as Language;
  } catch { /* ignored */ }
  return 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignored */ }
  }, []);

  const pluralRules = useMemo(
    () => new Intl.PluralRules(LOCALE_MAP[language] ?? 'en-US'),
    [language],
  );

  const t = useCallback((key: string): string => {
    const dict = translations[language] as Record<string, string>;
    const value = dict[key];
    if (value === undefined) {
      // In dev: throw so the missing key is immediately obvious.
      // In prod: warn once per missing key so telemetry / Sentry can
      // pick it up without breaking the UI. We still return the key
      // itself as a last-resort label.
      if (import.meta.env.DEV) {
        throw new Error(`[i18n] Missing translation key: "${key}" (language=${language})`);
      }
      console.warn(`[i18n] Missing translation key: "${key}" (language=${language})`);
      return key;
    }
    return value;
  }, [language]);

  const tCount = useCallback((key: string, count: number): string => {
    const dict = translations[language] as Record<string, string>;
    const category = pluralRules.select(count);
    const candidate = dict[`${key}_${category}`] ?? dict[`${key}_other`] ?? dict[key];
    if (candidate === undefined) return t(key); // triggers missing-key warn/throw
    return candidate.replace(/\{\{count\}\}/g, String(count));
  }, [language, pluralRules, t]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, tCount }}>
      {children}
    </LanguageContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTranslation() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useTranslation must be used within LanguageProvider');
  return ctx;
}
